import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { toast } from 'sonner';
import { 
  Users, 
  AlertTriangle,
  Shield,
  Lock,
  Activity,
  RefreshCw,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export default function AdminPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, demoRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/demo-status'),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setDemoMode(demoRes.data.demo_mode);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDemoMode = async () => {
    try {
      await api.post('/admin/toggle-demo', { demo_mode: !demoMode });
      setDemoMode(!demoMode);
      toast.success(`Demo mode ${!demoMode ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to toggle demo mode');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-black text-[#E6F1FF]">Admin Panel</h1>
            <p className="text-[#8892B0] mt-1">Enterprise administration dashboard</p>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-[#64FFDA]/20 text-[#8892B0] hover:bg-[#64FFDA]/10"
              data-testid="refresh-admin-button"
            >
              <RefreshCw size={18} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Demo Mode Toggle */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {demoMode ? (
                <ToggleRight className="w-8 h-8 text-[#10B981]" />
              ) : (
                <ToggleLeft className="w-8 h-8 text-[#8892B0]" />
              )}
              <div>
                <h3 className="text-lg font-bold text-[#E6F1FF]">Demo Mode</h3>
                <p className="text-sm text-[#8892B0]">
                  {demoMode ? 'Demo mode is active for presentations' : 'Demo mode is disabled'}
                </p>
              </div>
            </div>
            <Switch
              checked={demoMode}
              onCheckedChange={toggleDemoMode}
              className="data-[state=checked]:bg-[#10B981]"
              data-testid="demo-mode-toggle"
            />
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="admin-stat" data-testid="stat-total-users">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-8 h-8 text-[#2BBBAD]" />
              </div>
              <p className="admin-stat-value">{stats.users.total}</p>
              <p className="admin-stat-label">Total Users</p>
            </div>

            <div className="admin-stat" data-testid="stat-panic-users">
              <div className="flex items-center justify-between mb-4">
                <AlertTriangle className="w-8 h-8 text-[#FF3B30]" />
              </div>
              <p className="admin-stat-value text-[#FF3B30]">{stats.users.in_panic_mode}</p>
              <p className="admin-stat-label">In Panic Mode</p>
            </div>

            <div className="admin-stat" data-testid="stat-mfa-enabled">
              <div className="flex items-center justify-between mb-4">
                <Shield className="w-8 h-8 text-[#10B981]" />
              </div>
              <p className="admin-stat-value text-[#10B981]">{stats.users.mfa_enabled}</p>
              <p className="admin-stat-label">MFA Enabled</p>
            </div>

            <div className="admin-stat" data-testid="stat-panic-24h">
              <div className="flex items-center justify-between mb-4">
                <Activity className="w-8 h-8 text-[#FFCC00]" />
              </div>
              <p className="admin-stat-value text-[#FFCC00]">{stats.recent_activity.panic_activations_24h}</p>
              <p className="admin-stat-label">Panics (24h)</p>
            </div>
          </div>
        )}

        {/* Partner Stats */}
        {stats && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#E6F1FF] mb-4">Partner Statistics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-[#0B1C2D]/50">
                <p className="text-3xl font-black text-[#E6F1FF]">{stats.partners.total}</p>
                <p className="text-sm text-[#8892B0]">Total Partners</p>
              </div>
              <div className="p-4 rounded-xl bg-[#0B1C2D]/50">
                <p className="text-3xl font-black text-[#2BBBAD]">{stats.partners.protected}</p>
                <p className="text-sm text-[#8892B0]">Protected</p>
              </div>
              <div className="p-4 rounded-xl bg-[#0B1C2D]/50">
                <p className="text-3xl font-black text-[#FF3B30]">{stats.partners.locked}</p>
                <p className="text-sm text-[#8892B0]">Currently Locked</p>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-lg font-bold text-[#E6F1FF] mb-4">Users</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#64FFDA]/10">
                  <TableHead className="text-[#8892B0]">Name</TableHead>
                  <TableHead className="text-[#8892B0]">Email</TableHead>
                  <TableHead className="text-[#8892B0]">Role</TableHead>
                  <TableHead className="text-[#8892B0]">MFA</TableHead>
                  <TableHead className="text-[#8892B0]">Status</TableHead>
                  <TableHead className="text-[#8892B0]">Last Login</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-[#64FFDA]/10" data-testid={`user-row-${user.email}`}>
                    <TableCell className="text-[#E6F1FF] font-medium">{user.full_name}</TableCell>
                    <TableCell className="text-[#8892B0]">{user.email}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.is_admin ? 'bg-[#64FFDA]/20 text-[#64FFDA]' : 'bg-[#2BBBAD]/20 text-[#2BBBAD]'}`}>
                        {user.is_admin ? 'Admin' : 'User'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.mfa_enabled ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#8892B0]/20 text-[#8892B0]'}`}>
                        {user.mfa_enabled ? 'Enabled' : 'Off'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.panic_mode ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 'bg-[#10B981]/20 text-[#10B981]'}`}>
                        {user.panic_mode ? 'PANIC' : 'Safe'}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#8892B0] text-sm">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
